import type { MouseEvent, ReactNode } from 'react';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import { IconButton, Box, Collapse } from '@mui/material';

type IProps = {
  isCollapsed?: boolean;
  handleCollapseClick: (_event: MouseEvent<HTMLElement>) => void;
  children?: ReactNode;
  status?: string;
};

/**
 * Props for configuring a card footer collapsible component from [MicroLibraryCoreUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryCoreUI)
 *
 * @property {boolean} isCollapsed - Whether the card is collapsed.
 * @property {function} handleCollapseClick - The function to handle the collapse click.
 */
export const CardFooterCollapsible = ({ ...props }: IProps) => {
  const { isCollapsed, children, handleCollapseClick, status } = props;

  const getCollapsibleFooterBackground = () => {
    if (status === 'Commissionable') {
      return '#FCE8F2';
    }
    if (status === 'Commissioned') {
      return '#D1F8FB';
    }
    if (status === 'Promo') {
      return '#91D694';
    }
    return '#E7E7E7';
  };

  return (
    <Box
      sx={{
        backgroundColor: getCollapsibleFooterBackground(),
        position: 'relative',
        height: '100%',
        paddingBottom: '12px',
        ...(isCollapsed && {
          paddingBottom: 0,
        }),
      }}
    >
      <IconButton
        sx={{
          position: 'absolute',
          top: '-25px',
          left: 'calc(50% - 23px)',
        }}
        onClick={handleCollapseClick}
        onMouseDown={(event) => event.stopPropagation()}
        onTouchStart={(event) => event.stopPropagation()}
        aria-label={isCollapsed ? 'see less' : 'see more'}
        data-testid="app-card-collapsible-section-toggle"
      >
        <ArrowDropDownCircleIcon
          sx={{
            fontSize: '30px',
            transition: 'transform 300ms ease',
            ...(isCollapsed && {
              transform: 'rotate(180deg)',
            }),
          }}
          color="primary"
        />
      </IconButton>

      <Collapse
        sx={{
          boxShadow: 'inset 0px 8px 10px -6px #aaa',
        }}
        orientation="vertical"
        in={!isCollapsed}
        collapsedSize="110px"
      >
        {children}
      </Collapse>
    </Box>
  );
};
